import { Injectable } from '@angular/core';
import {
  AiActionItemsDataStreamEvent,
  AiAgentStepStreamEvent,
  AiChatStreamEvent,
  AiDoneStreamEvent,
  AiErrorStreamEvent,
  AiStatusStreamEvent,
  AiTaskContextDataStreamEvent,
  AiTaskLineageDataStreamEvent,
  AiTokenStreamEvent,
  AiToolResultStreamEvent,
} from '../models/ai-assistant.models';

interface ParsedSseFrame {
  eventName: string;
  data: string;
}

@Injectable({ providedIn: 'root' })
export class AiAssistantStreamParserService {
  private buffer = '';

  push(chunk: string): AiChatStreamEvent[] {
    this.buffer += chunk;
    const frames = this.consumeFrames();
    return frames
      .map((frame) => this.toStreamEvent(frame))
      .filter((event): event is AiChatStreamEvent => event !== null);
  }

  flush(): AiChatStreamEvent[] {
    if (!this.buffer.trim()) {
      this.buffer = '';
      return [];
    }

    const frame = this.parseFrame(this.buffer);
    this.buffer = '';
    if (!frame) {
      return [];
    }
    const event = this.toStreamEvent(frame);
    return event ? [event] : [];
  }

  reset(): void {
    this.buffer = '';
  }

  private consumeFrames(): ParsedSseFrame[] {
    const normalized = this.buffer.replace(/\r\n/g, '\n');
    const parts = normalized.split('\n\n');
    this.buffer = parts.pop() || '';

    return parts
      .map((part) => this.parseFrame(part))
      .filter((frame): frame is ParsedSseFrame => Boolean(frame));
  }

  private parseFrame(frameText: string): ParsedSseFrame | null {
    const lines = frameText.split('\n');
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }

    if (!dataLines.length) {
      return null;
    }

    return {
      eventName,
      data: dataLines.join('\n'),
    };
  }

  private toStreamEvent(frame: ParsedSseFrame): AiChatStreamEvent | null {
    const data = this.parseJson(frame.data);

    if (frame.eventName === 'token') {
      return {
        type: 'token',
        token: this.getString(data, 'token'),
      } satisfies AiTokenStreamEvent;
    }

    if (frame.eventName === 'tool_result') {
      const record = this.asRecord(data);
      return {
        type: 'tool_result',
        input: record['input'],
        output: this.asRecord(record['output']) as AiToolResultStreamEvent['output'],
      };
    }

    if (frame.eventName === 'task_context_data') {
      const record = this.asRecord(data);
      return {
        type: 'task_context_data',
        input: record['input'],
        output: this.asRecord(record['output']) as unknown as AiTaskContextDataStreamEvent['output'],
      };
    }

    if (frame.eventName === 'action_items_data') {
      const record = this.asRecord(data);
      return {
        type: 'action_items_data',
        input: record['input'],
        output: this.asRecord(record['output']) as unknown as AiActionItemsDataStreamEvent['output'],
      };
    }

    if (frame.eventName === 'task_lineage_data') {
      const record = this.asRecord(data);
      return {
        type: 'task_lineage_data',
        input: record['input'],
        output: this.asRecord(record['output']) as unknown as AiTaskLineageDataStreamEvent['output'],
      };
    }

    if (frame.eventName === 'agent_step') {
      const record = this.asRecord(data);
      const output = this.asRecord(record['output']);
      return {
        type: 'agent_step',
        agentId: typeof output['agentId'] === 'string' ? (output['agentId'] as string) : undefined,
        turn: typeof output['turn'] === 'number' ? (output['turn'] as number) : undefined,
        action: output['action'] as AiAgentStepStreamEvent['action'],
        toolName: (output['toolName'] as string | null | undefined) ?? undefined,
        reasoning: typeof output['reasoning'] === 'string' ? (output['reasoning'] as string) : undefined,
        status: output['status'] as AiAgentStepStreamEvent['status'],
      } satisfies AiAgentStepStreamEvent;
    }

    if (frame.eventName === 'done') {
      return {
        type: 'done',
        ...(this.asRecord(data) as Omit<AiDoneStreamEvent, 'type'>),
      };
    }

    if (frame.eventName === 'status') {
      return {
        type: 'status',
        message: this.getString(data, 'message'),
      } satisfies AiStatusStreamEvent;
    }

    if (frame.eventName === 'error') {
      const record = this.asRecord(data);
      const message = typeof record['message'] === 'string' ? (record['message'] as string) : 'The assistant ran into an error.';
      return {
        type: 'error',
        message,
        error: record['error'] ?? data,
      } satisfies AiErrorStreamEvent;
    }

    // Genuinely unrecognized event name (e.g. a stage-timing/telemetry event
    // this client doesn't render). Previously this was silently turned into a
    // fake `error` stream event, which surfaced telemetry noise to the user as
    // if the assistant had failed. Just ignore it instead.
    console.debug(`[ai-assistant] ignoring unhandled stream event: ${frame.eventName}`);
    return null;
  }

  private parseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getString(value: unknown, key: string): string {
    const record = this.asRecord(value);
    const nextValue = record[key];
    return typeof nextValue === 'string' ? nextValue : '';
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  }
}
